import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { useAsync, useWindowSize } from 'react-use';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import { abeStylists } from 'actions/stylist/beSalonStylists.action';
import { ibeCustomPermissions } from 'actions/user/beCustomPermissions.action';
import { ibeStylistPermissionsRead } from 'actions/user/beStylistPermissions.action';
import ICO_SERVICES from 'assets/images/online-booking/Services.svg';
import filterActiveStylistsAll from 'bl/data/filterActiveStylistsAll.bl';
import Badge from 'components/generic/Badge.component';
import Foldout from 'components/generic/Foldout.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import TruncatedText from 'components/generic/TruncatedText.component';
import PermissionField from 'components/settings/PermissionField.component';
import LINK from 'constants/link.const';
import USER_ROLE from 'constants/role';
import MOBILE_VIEWPORT from 'constants/ui/mobileViewport.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import noNilArray from 'utils/arrays/noNilArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromPropsToMatchedParams } from 'utils/converters/props/path.util';
import linkTo from 'utils/links/linkTo.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import { isEqualByCaseless } from 'utils/predicates/string/simple.util';

import CN from './StylistsPermissions.module.scss';

const CTAG = 'StylistsPermissions';
const L = logger(CTAG);

// NOTE: There will be a new API for stylists and their permissions, and the structure may change.
const determineTooltip = (stylist, t) =>
  stylist.permissionLevel === USER_ROLE.noAccess
    ? t('staffAndBusiness.stylistPermission.noAccessTooltip')
    : t('staffAndBusiness.stylistPermission.commingSoonTooltip');

const Title = ({ stylist }) => {
  const { permissionLevel: urole, userName } = stylist ?? {};
  const { t } = useTranslation();

  const { width } = useWindowSize();
  const isMobileViewport = width <= MOBILE_VIEWPORT;

  return (
    <div data-bem="Title--stylists-permissions" className={CN.header}>
      <TruncatedText max={isMobileViewport ? 28 : 62} suffix="..." value={userName} />
      <div className={CN.badgeContainer}>
        {USER_ROLE.manager === urole && (
          <Badge variant="success" height="sm" name={t('staffAndBusiness.stylistPermission.managerAccess')} />
        )}
        {USER_ROLE.receptionist === urole && (
          <Badge variant="fine" height="sm" name={t('staffAndBusiness.stylistPermission.receptionistAccess')} />
        )}
        {USER_ROLE.noAccess === urole && (
          <Badge variant="alert" height="sm" name={t('common.permissionRoles.noAccess')} />
        )}
      </div>
    </div>
  );
};

const SingleCategoryPane = ({ category, value, stylist }) => {
  const { t } = useTranslation();
  const stylistId = stylist?.id;
  const { name, description, permissions } = category ?? {};

  return (
    <div data-bem="SingleCategoryPane" className={CN.categoryContainer}>
      <div data-bem="SingleCategoryPane__title" className={CN.title}>
        {name}
        <div className={CN.description}> {description}</div>
      </div>

      <div data-bem="SingleCategoryPane__permission-field" className={CN.optionsContainer}>
        {noNilArray(permissions).map((definition) => (
          <PermissionField
            key={definition?.key}
            definition={definition}
            value={value}
            tooltip={determineTooltip(stylist, t)}
            stylistId={stylistId}
          />
        ))}
      </div>
    </div>
  );
};

const InnerCategoriesPane = ({ categories, stylist, ibeStylistPermissionsRead }) => {
  const { t } = useTranslation();
  const stylistId = stylist?.id;

  const {
    value,
    loading: isLoading,
    error,
  } = useAsync(async () => {
    // this action has UMSG handling, so it will display a global message upon error
    const result = await ibeStylistPermissionsRead({ stylistId });
    return result.is.ok ? result.json : null;
  }, [ibeStylistPermissionsRead, stylistId]);

  const actualCategories = noNilArray(categories);

  const enabledCategories = actualCategories.filter((c) => asArray(c?.permissions).every((p) => p.enabled));
  const disabledCategories = actualCategories.filter((c) => asArray(c?.permissions).some((p) => !p.enabled));

  L.debug('InnerCategoriesPane()', stylistId, isLoading, error, value);

  return (
    <>
      {enabledCategories.map((c) => (
        <SingleCategoryPane key={c.name} category={c} stylist={stylist} value={value} />
      ))}

      <div className={CN.banner}>{t('staffAndBusiness.stylistPermission.categoriesPaneBaner')}</div>

      {disabledCategories.map((c) => (
        <SingleCategoryPane key={c.name} category={c} stylist={stylist} value={value} />
      ))}
    </>
  );
};

const CategoriesPane = connect(null, (dispatch) => bindActionCreators({ ibeStylistPermissionsRead }, dispatch))(
  InnerCategoriesPane,
);

const InnerStylistPermissionsPane = (props) => {
  const { stylist, isOpen, categories } = props;
  const { t } = useTranslation();
  const stylistId = stylist?.id;

  const nav = navFor(CTAG, props);
  const salonId = useCurrentSalonId();

  const handleStylistToggle = useCallback(
    (ev) => {
      if (!ev?.isOpen) return;

      const params = { stylistId, salonId };

      if (!params.stylistId || !params.salonId) {
        return void L.warn('handleStylistToggle()', 'Problem navigating with params:', params);
      }

      nav.push({ pathname: linkTo({ pattern: LINK.permissions, params }) });
    },
    [nav, salonId, stylistId],
  );

  return (
    <div data-bem="StylistPermissionsPane">
      <Foldout
        title={<Title stylist={stylist} />}
        src={ICO_SERVICES}
        onToggle={handleStylistToggle}
        isOpen={isOpen}
        disabled={stylist.permissionLevel === USER_ROLE.noAccess} // Pass disabled prop based on permission level
        tooltipText={t('staffAndBusiness.stylistPermission.noAccessTooltip')}
      >
        <CategoriesPane categories={categories} stylist={stylist} />
      </Foldout>
    </div>
  );
};

const StylistPermissionsPane = withRouter(InnerStylistPermissionsPane);

const StylistsPermissionsPage = (props) => {
  const { abeStylists, ibeCustomPermissions } = props;

  const stylistIdFromUrl = fromAnyToTrimmed(fromPropsToMatchedParams(props)?.stylistId);

  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();

  const [stylists, setStylists] = useStateWhileMounted([]);

  const { value: valueCategories, loading: isLoadingCategories } = useAsync(async () => {
    const result = await ibeCustomPermissions();

    if (!result.is.ok) {
      // TODO: handle errors, display to user or at the very least to the browser console
      return [];
    }

    return result.json;
  }, [ibeCustomPermissions]);

  const { value: valueStylists, loading: isLoadingStylists } = useAsync(async () => {
    const result = await abeStylists(salonId);

    if (!result.is.ok) {
      // TODO: handle errors, display to user or at the very least to the browser console
      return [];
    }

    const rawStylists = result.json;
    const filteredStylists = filterActiveStylistsAll(rawStylists);
    L.debug('useAsync()', 'abeStylists result:', result);

    setStylists(filteredStylists);

    return filteredStylists;
  }, [abeStylists, salonId, setStylists]);

  L.debug('()', isLoadingStylists, isLoadingCategories, valueStylists, valueCategories);

  return (
    <div
      data-bem="StylistsPermissionsPage"
      className={cn({
        'services online-booking-layout': true,
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div data-bem="StylistsPermissions__container" className={CN.container}>
        <div className={CN.flex}>
          <div className={CN.pageTitle}>
            <div className={CN.pageIcon}>
              <Icon variant="shield" width="xl" />
            </div>
            {t('staffAndBusiness.stylistPermission.title')}
          </div>
          <div className={CN.subTitle}>{t('staffAndBusiness.stylistPermission.description')}</div>
        </div>
      </div>
      {isLoadingCategories ? (
        <OverlaySpinner />
      ) : (
        <div>
          {stylists.map((stylist) => (
            <StylistPermissionsPane
              key={stylist.id}
              stylist={stylist}
              isOpen={stylistIdFromUrl && stylist?.id && isEqualByCaseless(stylist?.id, stylistIdFromUrl)}
              categories={valueCategories}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const StylistsPermissionsPageHoc = connect(null, (dispatch) =>
  bindActionCreators(
    {
      abeStylists,
      ibeCustomPermissions,
    },
    dispatch,
  ),
)(StylistsPermissionsPage);

export default StylistsPermissionsPageHoc;
